import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { Gantt } from 'dhtmlx-gantt';
import 'dhtmlx-gantt/codebase/dhtmlxgantt.css';
import { Box } from '@mui/material';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import {
    fetchLegMembersByFlt,
    fetchGroupedRosterDetailsByCrewCode,
} from "state/api";
import CrewListDialog from 'components/CrewListDialog';
import RosterDetailsDialog from 'components/RosterDetailsDialog';
import { processData } from './processData';
import { configureGantt } from './configureGantt';

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;


const CrewGanttChart = ({
    rowHeight = 52,
    cellWidth = 18,
    splitterWidth = 220,
    rosterDetails = [],
}) => {
    const gantt = useMemo(() => Gantt.getGanttInstance(), []);

    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);

    const { startDate, endDate } = useDateRangeContext();

    const ganttContainer = useRef(null);

    const TIMEZONE = useMemo(
        () => params?.find((item) => item.param === "TIMEZONE")?.value || 'Asia/Ho_Chi_Minh',
        [params]
    );

    const [dialogState, setDialogState] = useState({
        openCrewList: false,
        flight: [],
        crewList: [],
        jumpList: [],
        openRosterDetails: false,
        crewCode: '',
        crewName: '',
        crewRosters: [],
    });

    const handleCloseCrewListDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, openCrewList: false }));
    }, []);

    const handleCloseRosterDetailsDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, openRosterDetails: false }));
    }, []);

    const handleViewCrewListClick = useCallback(async (flightDate, flightDesc, dep) => {
        try {
            const { flightDetails, legMembers, jumpSeaters } = await fetchLegMembersByFlt(flightDate, flightDesc, dep);
            setDialogState({
                openCrewList: true,
                flight: flightDetails,
                crewList: legMembers,
                jumpList: jumpSeaters,
            });
        } catch {
            setDialogState(prev => ({ ...prev, openCrewList: false }));
        }
    }, []);

    const handleViewRosterDetailsClick = useCallback(async (crewCode, crewName) => {
        try {
            const crewRosters = await fetchGroupedRosterDetailsByCrewCode(crewCode, startDate, endDate);
            setDialogState({
                openRosterDetails: true,
                crewCode,
                crewName,
                crewRosters,
            });
        } catch {
            setDialogState(prev => ({ ...prev, openRosterDetails: false }));
        }
    }, [startDate, endDate]);

    const showSubtasks = useCallback((task) => {
        if (!gantt.hasChild(task.id)) return false;

        const sub_tasks = gantt.getChildren(task.id);
        const subtasks = [task.text];

        for (let i = 0; i < sub_tasks.length; i++) {
            const child = gantt.getTask(sub_tasks[i]);
            const child_sizes = gantt.getTaskPosition(child);
            const left_position = child_sizes.left - gantt.getTaskPosition(task).left;
            const task_width = child_sizes.width;

            subtasks.push(`
                <span
                    class='subtask'
                    data-id='${child.id}'
                    data-sectorType='${child.sectorType}'
                    style='position: absolute; left: ${left_position}px; width: ${task_width}px;'
                >
                    ${child.text}
                </span>`);
        }

        setTimeout(() => {
            document.querySelectorAll('.subtask').forEach(span => {
                span.onclick = (e) => {
                    e.stopPropagation();
                    document.querySelectorAll('.subtask').forEach(s => s.classList.remove('selected-blinking'));
                    span.classList.add('selected-blinking');

                    const subtaskId = span.getAttribute('data-id');
                    if (subtaskId && OBJECT_ID_PATTERN.test(subtaskId)) {
                        const subtaskTask = gantt.getTask(subtaskId);
                        if (subtaskTask.flightCarrier) {
                            handleViewCrewListClick(subtaskTask.flightDate, subtaskTask.flightDesc, subtaskTask.dep);
                        }
                    }
                };
            });
        }, 0);

        return subtasks.join("");
    }, [gantt, handleViewCrewListClick]);

    const showSubtasksRef = useRef(showSubtasks);
    useEffect(() => {
        showSubtasksRef.current = showSubtasks;
    }, [showSubtasks]);

    const getNow = useCallback(() => {
        return timeMode === 'UTC'
            ? toZonedTime(new Date(), 'UTC')
            : toZonedTime(new Date(), TIMEZONE);
    }, [timeMode, TIMEZONE]);

    const drawNowLine = useCallback(() => {
        const container = ganttContainer.current?.querySelector(".gantt_task_bg");
        if (!container) return;

        const now = getNow();
        const state = gantt.getState();
        const isWithinRange = now >= state.min_date && now <= state.max_date;

        let nowLine = container.querySelector(".gantt_now_line");
        if (!isWithinRange) {
            nowLine?.remove();
            return;
        }

        if (!nowLine) {
            nowLine = document.createElement("div");
            nowLine.className = "gantt_now_line";
            container.appendChild(nowLine);
        }

        const position = gantt.posFromDate(now);
        Object.assign(nowLine.style, {
            left: `${position}px`,
            top: "0px",
            height: `${container.scrollHeight}px`,
            position: "absolute",
            width: "2px",
            backgroundColor: "red",
            zIndex: 9,
            pointerEvents: "none",
        });
    }, [gantt, getNow]);

    const drawWeekendColumns = useCallback(() => {
        const bgContainer = ganttContainer.current?.querySelector(".gantt_task_bg");
        if (!bgContainer) return;

        bgContainer.querySelectorAll(".weekend-column-layer").forEach(el => el.remove());

        const state = gantt.getState();
        const start = startDate
            ? new Date(new Date(startDate).setHours(0, 0, 0, 0))
            : new Date(state.min_date.setHours(0, 0, 0, 0));
        const end = endDate
            ? new Date(new Date(endDate).setHours(23, 59, 59, 999))
            : new Date(state.max_date.setHours(23, 59, 59, 999));

        let date = new Date(start);
        while (date <= end) {
            const day = date.getDay();
            if (day === 6 || day === 0) {
                const left = gantt.posFromDate(date);
                const right = gantt.posFromDate(gantt.date.add(date, 1, "day"));

                const div = document.createElement("div");
                div.className = "weekend-column-layer " + (day === 6 ? "weekend-saturday" : "weekend-sunday");
                Object.assign(div.style, {
                    left: `${left}px`,
                    top: "0px",
                    width: `${right - left}px`,
                    height: `${bgContainer.scrollHeight}px`,
                });
                bgContainer.appendChild(div);
            }
            date = gantt.date.add(date, 1, "day");
        }
    }, [gantt, startDate, endDate]);

    const setupCustomGridRendering = useCallback(() => {
        setTimeout(() => {
            document.querySelectorAll('.parent-grid-row').forEach(row => {
                const taskId = row.getAttribute('task_id');
                if (!taskId?.startsWith('parent_')) return;
                row.style.cursor = 'pointer';
                row.onclick = (e) => {
                    e.stopPropagation();
                    const task = gantt.getTask(taskId);
                    if (task?.crewCode && task?.crewName) {
                        handleViewRosterDetailsClick(task.crewCode, task.crewName);
                    }
                };
            });
        }, 100);
    }, [gantt, handleViewRosterDetailsClick]);

    useEffect(() => {
        const { tasks, links } = processData(rosterDetails, timeMode);

        const ganttEl = ganttContainer.current;
        if (!ganttEl) return;

        configureGantt(gantt, rowHeight, cellWidth, splitterWidth, showSubtasksRef);
        gantt.init(ganttEl);
        gantt.clearAll();
        gantt.parse({ data: tasks, links });

        const now = getNow();
        const ganttState = gantt.getState();
        if (now >= ganttState.min_date && now <= ganttState.max_date) {
            gantt.scrollTo(gantt.posFromDate(now) - gantt.config.task_scroll_offset, 0);
        }

        const safeClickHandler = (id) => {
            const idStr = String(id);
            if (idStr.startsWith('parent_')) return true;
            try {
                const task = gantt.getTask(id);
                if (!task.isParent && OBJECT_ID_PATTERN.test(idStr) && task.flightCarrier) {
                    handleViewCrewListClick(task.flightDate, task.flightDesc, task.dep);
                }
            } catch { /* ignore */ }
            return true;
        };

        const gridClickHandler = (id) => {
            try {
                const task = gantt.getTask(id);
                if (task.isParent && task.crewCode && task.crewName) {
                    handleViewRosterDetailsClick(task.crewCode, task.crewName);
                }
            } catch { /* ignore */ }
            return true;
        };

        const handleDocumentClick = (e) => {
            const parentRow = e.target.closest('.parent-grid-row');
            if (!parentRow) return;
            const taskId = parentRow.getAttribute('task_id');
            if (!taskId?.startsWith('parent_')) return;
            try {
                const task = gantt.getTask(taskId);
                if (task?.crewCode && task?.crewName) {
                    handleViewRosterDetailsClick(task.crewCode, task.crewName);
                }
            } catch { /* ignore */ }
        };

        drawWeekendColumns();
        drawNowLine();

        const scrollEventId = gantt.attachEvent("onGanttScroll", () => {
            drawNowLine();
            drawWeekendColumns();
        });
        gantt.attachEvent("onTaskClick", safeClickHandler);
        gantt.attachEvent("onGridClick", gridClickHandler);
        gantt.attachEvent("onDataRender", setupCustomGridRendering);
        gantt.attachEvent("onGridResizeEnd", (_oldWidth, newWidth) => {
            gantt.config.columns[0].width = newWidth;
            return true;
        });

        window.addEventListener("resize", drawNowLine);
        document.addEventListener('click', handleDocumentClick);
        const intervalId = setInterval(drawNowLine, 60000);

        return () => {
            gantt.clearAll();
            gantt.detachEvent(scrollEventId);
            window.removeEventListener("resize", drawNowLine);
            document.removeEventListener('click', handleDocumentClick);
            clearInterval(intervalId);
            ganttEl.querySelector(".gantt_task_bg")?.querySelector(".gantt_now_line")?.remove();
        };
    }, [
        gantt,
        timeMode,
        rowHeight,
        cellWidth,
        splitterWidth,
        rosterDetails,
        getNow,
        drawNowLine,
        drawWeekendColumns,
        handleViewCrewListClick,
        handleViewRosterDetailsClick,
        setupCustomGridRendering,
    ]);
    

    return (
        <Box>
            <div
                ref={ganttContainer}
                className="crew-gantt-container"
                style={{
                    width: '100%',
                    height: LAYOUT_HEIGHT,
                    '--row-height': `${rowHeight}`,
                }}
            />

            {dialogState.openCrewList &&
                <CrewListDialog
                    open={dialogState.openCrewList}
                    onClose={handleCloseCrewListDialog}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }
            {dialogState.openRosterDetails &&
                <RosterDetailsDialog
                    open={dialogState.openRosterDetails}
                    onClose={handleCloseRosterDetailsDialog}
                    crewCode={dialogState.crewCode}
                    crewName={dialogState.crewName}
                    crewRosters={dialogState.crewRosters}
                />
            }
        </Box>
    );
};

export default CrewGanttChart;
